import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Download, ShieldCheck, Trash2, Unlock } from "lucide-react";
import {
  adminOverview,
  adminReferrals,
  adminGrowth,
  redeemDiscountCode,
  setMemberPlan,
  clearPinLock,
  removeSubscriber,
} from "@/lib/admin.functions";

import { adminEarlyAccess, setEarlyAccessStatus } from "@/lib/early-access.functions";
import { getAccount } from "@/lib/account.functions";
import { PLAN_CODES, planOf } from "@/lib/plans";
import { MillionPlan } from "@/components/MillionPlan";
import { MetricsCheatSheet } from "@/components/MetricsCheatSheet";
import { AdsCommandCenter } from "@/components/AdsCommandCenter";
import { PublicThemePicker } from "@/components/PublicThemePicker";
import { BetaTesters } from "@/components/BetaTesters";
import { FeedbackInbox } from "@/components/FeedbackInbox";
import { adminFeedbackInbox } from "@/lib/admin-feedback.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({
    meta: [
      { title: "Control room — The Founders App admin" },
      { name: "description", content: "Site administration for The Founders App: members, plans, activity and mailing list." },
      { property: "og:title", content: "Control room — The Founders App admin" },
      { property: "og:description", content: "Members, plans, activity and mailing list." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminPage,
});

function Stat({
  label,
  value,
  hint,
  loading,
}: {
  label: string;
  value: string | number;
  hint?: string;
  loading?: boolean;
}) {
  return (
    <div className="soft-panel p-5 sm:p-6">
      <p className="micro-label">{label}</p>
      {loading ? (
        <Skeleton className="mt-3 h-8 w-20" />
      ) : (
        <p className="etched mt-3 text-3xl font-semibold tabular-nums">{value}</p>
      )}
      {hint && (
        loading ? (
          <Skeleton className="mt-2 h-3 w-32" />
        ) : (
          <p className="mt-1 text-[0.7rem] text-muted-foreground/70">{hint}</p>
        )
      )}
    </div>
  );
}


function AdminPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<
    "members" | "list" | "referrals" | "growth" | "early" | "beta" | "feedback" | "million" | "ads" | "cheat" | "look"
  >("million");
  const [search, setSearch] = useState("");
  const [busyEarly, setBusyEarly] = useState<string | null>(null);


  const account = useQuery({ queryKey: ["account"], queryFn: () => getAccount() });
  const overview = useQuery({
    queryKey: ["admin-overview"],
    queryFn: () => adminOverview(),
    enabled: account.data?.isAdmin === true,
  });

  const referrals = useQuery({
    queryKey: ["admin-referrals"],
    queryFn: () => adminReferrals(),
    enabled: account.data?.isAdmin === true,
  });

  const growth = useQuery({
    queryKey: ["admin-growth"],
    queryFn: () => adminGrowth(),
    enabled: account.data?.isAdmin === true,
  });

  const early = useQuery({
    queryKey: ["admin-early-access"],
    queryFn: () => adminEarlyAccess(),
    enabled: account.data?.isAdmin === true,
  });

  const feedback = useQuery({
    queryKey: ["admin-feedback"],
    queryFn: () => adminFeedbackInbox(),
    enabled: account.data?.isAdmin === true,
    refetchInterval: 60_000,
  });

  const unreadFeedback = feedback.data?.unread ?? 0;
  const newSignups = feedback.data?.newSignups ?? 0;

  const pendingEarly = (early.data?.requests ?? []).filter((r) => r.status === "pending").length;



  const setPlan = useServerFn(setMemberPlan);
  const unlockPin = useServerFn(clearPinLock);
  const dropSubscriber = useServerFn(removeSubscriber);
  const redeemCode = useServerFn(redeemDiscountCode);
  const setEarlyStatus = useServerFn(setEarlyAccessStatus);


  useEffect(() => {
    if (account.data && !account.data.isAdmin) void navigate({ to: "/app/today" });
  }, [account.data, navigate]);

  const stats = overview.data?.stats;
  const members = overview.data?.members ?? [];
  const subscribers = overview.data?.subscribers ?? [];

  const filtered = useMemo(
    () =>
      members.filter((m) =>
        search ? (m.email ?? "").toLowerCase().includes(search.toLowerCase()) : true,
      ),
    [members, search],
  );

  const refRows = referrals.data?.rows ?? [];
  const refTotals = referrals.data?.totals ?? null;
  const conversion = refTotals?.total_members
    ? Math.round((refTotals.referred_members / refTotals.total_members) * 100)
    : 0;

  const peak = Math.max(1, ...(stats?.daily_series ?? []).map((d) => d.count));

  const mrr = members.reduce((sum, m) => {
    const price = { commitment: 29, operator: 79, vault: 149 }[m.plan as "commitment"] ?? 0;
    return sum + price;
  }, 0);

  const decideEarly = async (id: string, status: "approved" | "declined", email: string) => {
    setBusyEarly(id);
    try {
      await setEarlyStatus({ data: { id, status } });
      toast.success(
        status === "approved" ? `${email} can now create an account` : "Marked as declined",
      );
      await early.refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't update that request");
    } finally {
      setBusyEarly(null);
    }
  };

  const changePlan = async (userId: string, plan: string) => {

    try {
      await setPlan({ data: { userId, plan } });
      toast.success("Plan updated");
      await overview.refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't update plan");
    }
  };

  const exportCsv = () => {
    const rows = [
      ["email", "plan", "joined", "businesses", "entries", "last_active"],
      ...members.map((m) => [
        m.email ?? "",
        m.plan,
        m.created_at,
        String(m.businesses),
        String(m.entries),
        m.last_active ?? "",
      ]),
    ];
    const cell = (c: unknown) => {
      let s = String(c);
      // Neutralise spreadsheet formulas (=, +, -, @, tab, CR) planted in member data.
      if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
      return `"${s.replace(/"/g, '""')}"`;
    };
    const csv = rows.map((r) => r.map(cell).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "momentum-members.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const loading = account.isLoading || (account.data?.isAdmin === true && overview.isLoading);

  if (!loading && !account.data?.isAdmin) return null;


  return (
    <div className="relative w-full max-w-full overflow-x-clip">
      <div className="aurora !inset-0" aria-hidden />

      <header className="sticky top-0 z-20 border-b border-border bg-background/85 backdrop-blur">
        <div className="mx-auto flex w-full max-w-6xl min-w-0 items-center justify-between gap-4 px-5 py-4 sm:px-8">
          <div className="min-w-0">
            <p className="micro-label flex items-center gap-2">
              <ShieldCheck className="h-3 w-3" /> Control room
            </p>
            <h1 className="mt-1 truncate text-xl font-medium">Site administration</h1>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {pendingEarly > 0 && (
              <button
                onClick={() => setTab("early")}
                className="flex items-center gap-2 rounded-sm bg-amber-500 px-3 py-2 text-xs font-semibold text-black transition-opacity hover:opacity-90"
                title="Pending early-access requests"
              >
                <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-black/85 px-1 text-[0.65rem] tabular-nums text-amber-400">
                  {pendingEarly}
                </span>
                <span className="hidden sm:inline">pending</span>
              </button>
            )}
            <Link
              to="/app/today"
              className="flex items-center gap-2 rounded-sm border border-border px-3 py-2 text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> App
            </Link>
          </div>
        </div>
        {loading && (
          <div className="h-0.5 w-full overflow-hidden bg-border">
            <div className="h-full w-1/3 animate-[admin-load_1.1s_ease-in-out_infinite] bg-foreground/60" />
          </div>
        )}
      </header>
      <style>{`@keyframes admin-load{0%{transform:translateX(-100%)}100%{transform:translateX(300%)}}`}</style>

      <main id="main" className="relative mx-auto w-full max-w-6xl min-w-0 px-5 py-8 sm:px-8 sm:py-10">
        {pendingEarly > 0 && (
          <section
            className="mb-6 rounded-sm border border-amber-500/60 bg-amber-500/10 p-5 sm:p-6"
            aria-live="polite"
          >
            <div className="flex flex-wrap items-center gap-3">
              <span className="inline-flex h-7 min-w-7 items-center justify-center rounded-full bg-amber-500 px-2 text-sm font-semibold tabular-nums text-black">
                {pendingEarly}
              </span>
              <p className="text-sm font-medium text-foreground">
                {pendingEarly === 1
                  ? "1 early-access request waiting for review"
                  : `${pendingEarly} early-access requests waiting for review`}
              </p>
              <button
                onClick={() => setTab("early")}
                className="ml-auto rounded-sm border border-amber-500/60 px-3 py-1.5 text-xs text-foreground transition-colors hover:bg-amber-500/15"
              >
                Open full queue
              </button>
            </div>

            <ul className="mt-4 space-y-2">
              {(early.data?.requests ?? [])
                .filter((r) => r.status === "pending")
                .slice(0, 5)
                .map((r) => (
                  <li
                    key={r.id}
                    className="flex flex-wrap items-center gap-3 rounded-sm border border-border/60 bg-background/60 px-4 py-3"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="notranslate truncate text-sm text-foreground">{r.name}</p>
                      <p className="notranslate truncate text-[0.7rem] text-muted-foreground/70">
                        {r.email}
                      </p>
                      <p className="notranslate mt-1 line-clamp-2 text-[0.75rem] text-muted-foreground/80">
                        {r.business}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Button
                        size="sm"
                        disabled={busyEarly === r.id}
                        onClick={() => void decideEarly(r.id, "approved", r.email)}
                      >
                        Grant access
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busyEarly === r.id}
                        onClick={() => void decideEarly(r.id, "declined", r.email)}
                      >
                        Decline
                      </Button>
                    </div>
                  </li>
                ))}
            </ul>
            {pendingEarly > 5 && (
              <p className="mt-3 text-xs text-muted-foreground">
                +{pendingEarly - 5} more in the full queue.
              </p>
            )}
          </section>
        )}

        {loading && (
          <p className="mb-4 text-xs text-muted-foreground" role="status" aria-live="polite">
            Loading control room data…
          </p>
        )}
        {overview.isError && (
          <p className="mb-4 text-xs text-destructive" role="alert">
            Could not load control room data: {(overview.error as Error)?.message ?? "unknown error"}
          </p>
        )}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat loading={loading} label="Members" value={stats?.members ?? 0} hint={`${stats?.new_members_30d ?? 0} new in 30 days`} />
          <Stat loading={loading} label="Active · 7d" value={stats?.active_7d ?? 0} hint="logged a move this week" />
          <Stat loading={loading} label="Businesses" value={stats?.businesses ?? 0} />
          <Stat loading={loading} label="Committed MRR" value={`$${mrr.toLocaleString()}`} hint="at listed plan prices" />
        </div>

        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat
            loading={loading}
            label="Daily moves"
            value={stats?.daily_entries ?? 0}
            hint={`${stats?.daily_entries_closed ?? 0} closed out at night`}
          />
          <Stat loading={loading} label="Bold moves" value={stats?.bold_moves ?? 0} />
          <Stat loading={loading} label="Journal + contacts" value={(stats?.journal_entries ?? 0) + (stats?.contacts ?? 0)} />
          <Stat loading={loading} label="Mailing list" value={stats?.subscribers ?? 0} />
        </div>

        <section className="vault-panel mt-6 min-w-0 p-6 sm:p-7">
          <div className="flex items-baseline justify-between">
            <p className="micro-label">Daily moves · last 30 days</p>
            {!loading && (
              <span className="text-[0.7rem] text-muted-foreground/60 tabular-nums">peak {peak}</span>
            )}
          </div>
          <div className="mt-6 flex h-28 items-end gap-1">
            {loading ? (
              Array.from({ length: 30 }).map((_, i) => (
                <Skeleton
                  key={i}
                  className="flex-1 rounded-t-[2px]"
                  style={{ height: `${20 + ((i * 37) % 70)}%` }}
                />
              ))
            ) : (
              <>
                {(stats?.daily_series ?? []).map((d) => (
                  <div
                    key={d.day}
                    title={`${d.day}: ${d.count}`}
                    className="flex-1 rounded-t-[2px] bg-foreground/25 transition-colors hover:bg-foreground/60"
                    style={{ height: `${Math.max(4, (d.count / peak) * 100)}%` }}
                  />
                ))}
                {(stats?.daily_series ?? []).length === 0 && (
                  <p className="text-sm text-muted-foreground">No activity recorded yet.</p>
                )}
              </>
            )}
          </div>
        </section>

        {(unreadFeedback > 0 || newSignups > 0) && (
          <button
            type="button"
            onClick={() => setTab(unreadFeedback > 0 ? "feedback" : "members")}
            className="mt-6 flex w-full flex-wrap items-center gap-x-4 gap-y-2 rounded-sm border-2 border-amber-500 bg-amber-500/10 px-5 py-4 text-left"
          >
            <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-wider text-black">
              Needs you
            </span>
            <span className="text-sm font-medium">
              {unreadFeedback > 0 && (
                <>
                  {unreadFeedback} unread {unreadFeedback === 1 ? "note" : "notes"} from testers
                </>
              )}
              {unreadFeedback > 0 && newSignups > 0 && " · "}
              {newSignups > 0 && (
                <>
                  {newSignups} new {newSignups === 1 ? "signup" : "signups"} this week
                  {(feedback.data?.newBeta ?? 0) > 0 ? ` (${feedback.data?.newBeta} beta)` : ""}
                </>
              )}
            </span>
            <span className="text-xs text-muted-foreground">Open</span>
          </button>
        )}

        {loading ? (
          <section className="mt-6 space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </section>
        ) : (
        <section className="mt-6 min-w-0 max-w-full">

          <div className="flex flex-wrap items-center gap-3 border-b border-border">
            {(["feedback", "million", "ads", "look", "cheat", "members", "beta", "early", "list", "referrals", "growth"] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={cn(
                  "-mb-px flex items-center gap-2 border-b-2 border-transparent py-3 text-[0.7rem] font-semibold tracking-[0.18em] uppercase transition-colors",
                  tab === t ? "border-primary text-foreground" : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t === "feedback"
                  ? "Feedback"
                  : t === "members"
                  ? `Members · ${members.length}`
                  : t === "early"
                    ? "Early access"
                    : t === "beta"
                      ? "Beta testers"
                      : t === "list"
                      ? `Mailing list · ${subscribers.length}`
                      : t === "referrals"
                        ? `Referrals · ${refTotals?.referrals ?? 0}`
                        : t === "growth"
                          ? `Growth · ${(growth.data?.inquiries.length ?? 0) + (growth.data?.reservations.length ?? 0)}`
                          : t === "ads"
                            ? "Ads & ROI"
                            : t === "look"
                              ? "Site look"
                              : t === "cheat"
                              ? "Cheat sheet"
                              : "Road to $1M"}
                {(t === "early" || t === "feedback") && (
                  <span
                    className={cn(
                      "inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[0.65rem] tabular-nums",
                      (t === "feedback" ? unreadFeedback : pendingEarly) > 0
                        ? "bg-amber-500 font-bold text-black"
                        : "bg-foreground/10 text-muted-foreground",
                    )}
                  >
                    {t === "feedback" ? unreadFeedback : pendingEarly}
                  </span>
                )}
              </button>
            ))}

            <div className="ml-auto flex items-center gap-2 py-2">
              {tab === "members" && (
                <>
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search email"
                    className="h-9 w-40 sm:w-56"
                  />
                  <Button variant="outline" size="sm" onClick={exportCsv}>
                    <Download className="mr-2 h-3.5 w-3.5" /> CSV
                  </Button>
                </>
              )}
            </div>
          </div>

          {tab === "members" ? (
            <div className="mt-4 w-full max-w-full overflow-x-auto overscroll-x-contain rounded-sm border border-border/60">
              <table className="w-full min-w-[46rem] text-sm">
                <thead className="bg-background">

                  <tr className="text-left text-[0.65rem] tracking-[0.18em] text-muted-foreground/60 uppercase">
                    <th className="px-5 py-4 font-medium">Member</th>
                    <th className="px-5 py-4 font-medium">Plan</th>
                    <th className="px-5 py-4 font-medium">Businesses</th>
                    <th className="px-5 py-4 font-medium">Moves</th>
                    <th className="px-5 py-4 font-medium">Last active</th>
                    <th className="px-5 py-4 font-medium">PIN</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((m) => (
                    <tr key={m.user_id} className="border-t border-border/60">
                      <td className="px-5 py-4">
                        <p className="notranslate truncate text-foreground">{m.email ?? "—"}</p>
                        <p className="text-[0.7rem] text-muted-foreground/60">
                          joined {new Date(m.created_at).toLocaleDateString()}
                        </p>
                      </td>
                      <td className="px-5 py-4">
                        <select
                          value={m.plan}
                          onChange={(e) => void changePlan(m.user_id, e.target.value)}
                          className="rounded-sm border border-border bg-transparent px-2 py-1.5 text-xs text-foreground"
                        >
                          {PLAN_CODES.map((code) => (
                            <option key={code} value={code} className="bg-background">
                              {planOf(code).name} · {planOf(code).price}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-5 py-4 tabular-nums text-muted-foreground">{m.businesses}</td>
                      <td className="px-5 py-4 tabular-nums text-muted-foreground">{m.entries}</td>
                      <td className="px-5 py-4 text-muted-foreground">
                        {m.last_active ? new Date(m.last_active).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-5 py-4 text-muted-foreground">{m.has_pin ? "Set" : "—"}</td>
                      <td className="px-5 py-4 text-right">
                        <button
                          onClick={async () => {
                            await unlockPin({ data: { userId: m.user_id } });
                            toast.success("Lockout cleared");
                          }}
                          className="rounded-sm p-2 text-muted-foreground hover:text-foreground"
                          aria-label="Clear PIN lockout"
                          title="Clear PIN lockout"
                        >
                          <Unlock className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-5 py-8 text-sm text-muted-foreground">
                        No members match that search.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <p className="border-t border-border/60 px-5 py-4 text-[0.7rem] leading-relaxed text-muted-foreground/60">
                Member writing — daily moves, journal entries, contacts — stays private. This room
                only ever shows counts and plan state.
              </p>

            </div>
          ) : tab === "list" ? (
            <div className="mt-4 space-y-px">
              {subscribers.map((s) => (
                <div
                  key={s.id}
                  className="flex items-center justify-between gap-4 border-b border-border/60 py-3"
                >
                  <div className="min-w-0">
                    <p className="notranslate truncate text-sm">{s.email}</p>
                    <p className="text-[0.7rem] text-muted-foreground/60">
                      {new Date(s.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      await dropSubscriber({ data: { id: s.id } });
                      await overview.refetch();
                    }}
                    className="rounded-sm p-2 text-muted-foreground hover:text-destructive"
                    translate="no"
                    aria-label={`Remove ${s.email}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              {subscribers.length === 0 && (
                <p className="px-4 py-8 text-sm text-muted-foreground">No signups yet.</p>
              )}
            </div>
          ) : null}

          {tab === "referrals" && (
            <div className="mt-4">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <Stat
                  label="Referred signups"
                  value={refTotals?.referrals ?? 0}
                  hint={`${refTotals?.referrers ?? 0} members have referred someone`}
                />
                <Stat
                  label="Conversion"
                  value={`${conversion}%`}
                  hint={`${refTotals?.referred_members ?? 0} of ${refTotals?.total_members ?? 0} members joined via invite`}
                />
                <Stat
                  label="Free months available"
                  value={refTotals?.credits_available ?? 0}
                  hint={`${refTotals?.credits_used ?? 0} already applied`}
                />
                <Stat
                  label="Pending invites"
                  value={refTotals?.pending_invites ?? 0}
                  hint="codes held server-side, awaiting signup"
                />
              </div>

              <div className="mt-4 w-full max-w-full overflow-x-auto overscroll-x-contain rounded-sm border border-border/60">
                <table className="w-full min-w-[46rem] text-sm">
                  <thead className="bg-background">

                    <tr className="text-left text-[0.65rem] tracking-[0.18em] text-muted-foreground/60 uppercase">
                      <th className="px-4 py-3 font-medium">Referrer</th>
                      <th className="px-4 py-3 font-medium">Code</th>
                      <th className="px-4 py-3 font-medium">Joined</th>
                      <th className="px-4 py-3 font-medium">Credits left</th>
                      <th className="px-4 py-3 font-medium">Credits used</th>
                      <th className="px-4 py-3 font-medium">Billing paid through</th>
                      <th className="px-4 py-3 font-medium">Invitees</th>
                    </tr>
                  </thead>
                  <tbody>
                    {refRows.map((r) => (
                      <tr key={r.user_id} className="border-t border-border/60 align-top">
                        <td className="px-4 py-3">
                          <p className="notranslate truncate text-foreground">{r.email ?? "—"}</p>
                          {r.last_joined_at && (
                            <p className="text-[0.7rem] text-muted-foreground/60">
                              last join {new Date(r.last_joined_at).toLocaleDateString()}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                          {r.referral_code ?? "—"}
                        </td>
                        <td className="px-4 py-3 tabular-nums text-muted-foreground">{r.joined}</td>
                        <td className="px-4 py-3 tabular-nums text-muted-foreground">
                          {r.credits_available}
                        </td>
                        <td className="px-4 py-3 tabular-nums text-muted-foreground">
                          {r.credits_used}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {r.billing_period_end
                            ? new Date(r.billing_period_end).toLocaleDateString()
                            : "—"}
                        </td>
                        <td className="px-4 py-3 text-[0.7rem] text-muted-foreground/70">
                          {(r.invitees ?? []).slice(0, 5).map((i, idx) => (
                            <p key={`${r.user_id}-${idx}`} className="truncate">
                              <span className="notranslate">{i.email ?? "—"}</span> · {new Date(i.joined_at).toLocaleDateString()}
                            </p>
                          ))}
                          {(r.invitees ?? []).length > 5 && (
                            <p>+{(r.invitees ?? []).length - 5} more</p>
                          )}
                          {(r.invitees ?? []).length === 0 && <p>—</p>}
                        </td>
                      </tr>
                    ))}
                    {refRows.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-sm text-muted-foreground">
                          No referrals recorded yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === "early" && (
            <div className="mt-4 space-y-6">
              <div className="grid gap-3 sm:grid-cols-3">
                <Stat
                  loading={early.isLoading}
                  label="Pending"
                  value={(early.data?.requests ?? []).filter((r) => r.status === "pending").length}
                  hint="waiting on your call"
                />
                <Stat
                  loading={early.isLoading}
                  label="Approved"
                  value={(early.data?.requests ?? []).filter((r) => r.status === "approved").length}
                  hint="can create an account"
                />
                <Stat
                  loading={early.isLoading}
                  label="Signed up"
                  value={(early.data?.requests ?? []).filter((r) => r.claimed_user_id).length}
                  hint="approved and in the app"
                />
              </div>

              <ul className="space-y-2">
                {(early.data?.requests ?? []).map((r) => (
                  <li
                    key={r.id}
                    className="rounded-sm border border-border/60 bg-background/40 p-4 sm:p-5"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="notranslate text-sm text-foreground">{r.name}</p>
                        <p className="notranslate text-[0.7rem] break-all text-muted-foreground/70">
                          {r.email}
                        </p>
                      </div>
                      <div className="text-right text-[0.7rem] text-muted-foreground/60">
                        <p className="text-foreground/80">{r.status}</p>
                        <p>{new Date(r.created_at).toLocaleDateString()}</p>
                        {r.claimed_at ? (
                          <p>joined {new Date(r.claimed_at).toLocaleDateString()}</p>
                        ) : null}
                      </div>
                    </div>
                    <p className="notranslate mt-3 text-[0.8rem] text-muted-foreground/80">
                      {r.business}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {r.status !== "approved" && (
                        <Button
                          size="sm"
                          disabled={busyEarly === r.id}
                          onClick={() => void decideEarly(r.id, "approved", r.email)}
                        >
                          Grant access
                        </Button>
                      )}
                      {r.status !== "declined" && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyEarly === r.id}
                          onClick={() => void decideEarly(r.id, "declined", r.email)}
                        >
                          Decline
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
                {(early.data?.requests ?? []).length === 0 && (
                  <li className="rounded-sm border border-border/60 px-4 py-8 text-sm text-muted-foreground">
                    No early-access requests yet.
                  </li>
                )}
              </ul>

              <p className="text-[0.75rem] text-muted-foreground/70">
                Founders Edition reservations stay separate — approve those by hand from the Growth
                tab.
              </p>
            </div>
          )}

          {tab === "growth" && (
            <div className="mt-4 space-y-8">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <Stat
                  loading={growth.isLoading}
                  label="Quote requests"
                  value={growth.data?.inquiries.length ?? 0}
                  hint="team / bulk licence enquiries"
                />
                <Stat
                  loading={growth.isLoading}
                  label="Seats requested"
                  value={(growth.data?.inquiries ?? []).reduce((n, i) => n + i.seats, 0)}
                  hint="across all open quotes"
                />
                <Stat
                  loading={growth.isLoading}
                  label="Discount codes live"
                  value={
                    (growth.data?.codes ?? []).filter(
                      (c) => !c.redeemed_at && new Date(c.expires_at).getTime() > Date.now(),
                    ).length
                  }
                  hint={`${(growth.data?.codes ?? []).length} issued all time`}
                />
                <Stat
                  loading={growth.isLoading}
                  label="Founder spots claimed"
                  value={
                    (growth.data?.reservations ?? []).filter((r) => r.status !== "declined").length
                  }
                  hint="of 400"
                />
              </div>

              <div>
                <p className="micro-label">Bulk licence quotes</p>
                <div className="mt-3 w-full max-w-full overflow-x-auto overscroll-x-contain rounded-sm border border-border/60">
                  <table className="w-full min-w-[42rem] text-sm">
                    <thead className="bg-background">
                      <tr className="text-left text-[0.65rem] tracking-[0.18em] text-muted-foreground/60 uppercase">
                        <th className="px-4 py-3 font-medium">Company</th>
                        <th className="px-4 py-3 font-medium">Contact</th>
                        <th className="px-4 py-3 font-medium">Seats</th>
                        <th className="px-4 py-3 font-medium">Notes</th>
                        <th className="px-4 py-3 font-medium">Received</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(growth.data?.inquiries ?? []).map((i) => (
                        <tr key={i.id} className="border-t border-border/60 align-top">
                          <td className="notranslate px-4 py-3 text-foreground">{i.company}</td>
                          <td className="notranslate px-4 py-3 text-muted-foreground">
                            {i.contact_name}
                            <p className="text-[0.7rem] text-muted-foreground/60">{i.email}</p>
                          </td>
                          <td className="px-4 py-3 tabular-nums text-muted-foreground">{i.seats}</td>
                          <td className="notranslate px-4 py-3 text-[0.75rem] text-muted-foreground/80">
                            {i.message ?? "—"}
                          </td>
                          <td className="px-4 py-3 text-[0.7rem] text-muted-foreground/60">
                            {new Date(i.created_at).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                      {(growth.data?.inquiries ?? []).length === 0 && (
                        <tr>
                          <td colSpan={5} className="px-4 py-8 text-sm text-muted-foreground">
                            No quote requests yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <p className="micro-label">Discount codes</p>
                <div className="mt-3 w-full max-w-full overflow-x-auto overscroll-x-contain rounded-sm border border-border/60">
                  <table className="w-full min-w-[42rem] text-sm">
                    <thead className="bg-background">
                      <tr className="text-left text-[0.65rem] tracking-[0.18em] text-muted-foreground/60 uppercase">
                        <th className="px-4 py-3 font-medium">Code</th>
                        <th className="px-4 py-3 font-medium">Email</th>
                        <th className="px-4 py-3 font-medium">Off</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {(growth.data?.codes ?? []).map((c) => {
                        const expired = new Date(c.expires_at).getTime() <= Date.now();
                        const usable = !c.redeemed_at && !expired;
                        return (
                          <tr key={c.id} className="border-t border-border/60">
                            <td className="px-4 py-3 font-mono text-xs text-foreground">{c.code}</td>
                            <td className="notranslate px-4 py-3 truncate text-muted-foreground">
                              {c.email}
                            </td>
                            <td className="px-4 py-3 tabular-nums text-muted-foreground">
                              {c.percent}%
                            </td>
                            <td className="px-4 py-3 text-[0.75rem] text-muted-foreground">
                              {c.redeemed_at
                                ? `Used ${new Date(c.redeemed_at).toLocaleDateString()}`
                                : expired
                                  ? "Expired"
                                  : `Valid until ${new Date(c.expires_at).toLocaleString()}`}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {usable && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={async () => {
                                    const res = await redeemCode({ data: { id: c.id } });
                                    if (!res.ok) toast.error(res.message);
                                    else toast.success("Code marked as used");
                                    await growth.refetch();
                                  }}
                                >
                                  Mark used
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {(growth.data?.codes ?? []).length === 0 && (
                        <tr>
                          <td colSpan={5} className="px-4 py-8 text-sm text-muted-foreground">
                            No codes issued yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <p className="micro-label">Founder reservations</p>
                <div className="mt-3 space-y-px">
                  {(growth.data?.reservations ?? []).map((r) => (
                    <div key={r.id} className="border-b border-border/60 py-3">
                      <p className="notranslate text-sm text-foreground">
                        {r.display_name}
                        {r.handle ? ` · ${r.handle}` : ""}
                      </p>
                      <p className="notranslate text-[0.7rem] text-muted-foreground/60">
                        {r.email} · {new Date(r.created_at).toLocaleDateString()} · {r.status}
                        {r.show_publicly ? " · on the wall" : " · private"}
                      </p>
                      {r.note && (
                        <p className="notranslate mt-1 text-[0.75rem] text-muted-foreground/80">
                          {r.note}
                        </p>
                      )}
                    </div>
                  ))}
                  {(growth.data?.reservations ?? []).length === 0 && (
                    <p className="px-4 py-8 text-sm text-muted-foreground">No reservations yet.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {tab === "million" && (
            <MillionPlan
              planCounts={stats?.plans ?? {}}
              newMembers30d={stats?.new_members_30d ?? 0}
            />
          )}

          {tab === "beta" && <BetaTesters />}

          {tab === "feedback" && <FeedbackInbox />}

          {tab === "look" && <PublicThemePicker />}

          {tab === "cheat" && <MetricsCheatSheet />}

          {tab === "ads" && (
            <AdsCommandCenter
              payingMembers={
                members.filter((m) => ["commitment", "operator", "vault"].includes(m.plan)).length
              }
            />
          )}
        </section>

        )}

      </main>
    </div>
  );
}
